import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import AddEntry from '../trips/AddEntry.jsx'
import AddPlace from '../trips/AddPlace.jsx'
import CategoryRow from '../trips/CategoryRow.jsx'
import Estimate from '../trips/Estimate.jsx'
import TierBadge from '../components/TierBadge.jsx'
import { CATEGORIES } from '../trips/estimate.js'
import { splitPlaces } from '../trips/compare.js'
import {
  addEntry,
  addPlace,
  editEntry,
  loadBoard,
  loadEnrichment,
  setPreference,
  unfurlEntry,
} from '../trips/api.js'
import WeatherStrip from '../trips/WeatherStrip.jsx'
import { distancesByEntry } from '../trips/enrichment.js'
import { openLiveChannel } from '../trips/live.js'
import { onlyYouCanSee, visibilityLine } from '../trips/people.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The Trip Board — P5-3 T4.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 PROGRESSIVE DISCLOSURE: THIS FILE RENDERS NO DESTINATION CONCEPT AT ALL
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF calls it the hardest rule and states it as an absence: "With zero or
// one destination the concept is completely invisible: no destination column,
// no compare tab, no grouping, no 'ungrouped' bucket, no empty state
// advertising the feature. The board is a plain grid."
//
// The board therefore groups by CATEGORY and by nothing else, and this
// component does not read `board.destinations` or the estimate's
// `by_destination` at all — both arrive in the payload and both are ignored.
// That is not laziness: T9 owns the compare screen, it appears only at two or
// more, and "most trips are single-destination and must not pay a tax for this
// feature existing."
//
// ⚠️ The rule is a claim about RENDERED OUTPUT, so it is tested as one.
// src/test/trips/progressive-disclosure.test.jsx renders this component with a
// trip that HAS a destination — with a sentinel name — and asserts that neither
// the word "destination" nor that name appears anywhere in the markup. Testing
// that a particular component is absent would pass for the wrong reason the
// first time somebody renamed it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE LIVE CHANNEL — P5-3 T5 — AND WHY IT IS THREE LINES IN THIS FILE
// ═══════════════════════════════════════════════════════════════════════════
//
// `src/trips/live.js` owns the transports: an SSE tail of the changes feed on
// a direct browser↔service connection, falling back to a `?since=` poll of the
// same feed, automatically and silently (§8). What arrives here is one thing —
// *"something changed"* — and the response is to re-read the board.
//
// It is NOT a patch applied to local state, and that is the same argument T4
// already made about this member's own writes: a change moves the estimate
// (coverage, the per-currency subtotals, which flat candidate the roll-up
// used), none of it derivable in the browser without reimplementing cost.py,
// and a board that patched one card would show "amounts on 8 of 12" beside
// thirteen. Somebody else's write is the same write.
//
// ⚠️ TWO PROPERTIES BELOW EXIST ONLY BECAUSE REFRESHES NOW ARRIVE UNBIDDEN,
// and both are failures this file would otherwise have introduced:
//
//   1. **A background refresh never tears down a working board.** T4's
//      `refresh` replaced the whole page with an error notice on any failure —
//      correct when the only refreshes were ones the member had just asked
//      for. A live channel means a blip at a moment nobody touched anything
//      would blank the board. §8 says the fallback engages *"with no
//      user-visible ceremony"*, and a board that vanishes is ceremony.
//   2. **Refreshes are generation-tagged, and only the newest is applied.** A
//      live refresh started before a write and answered after it would put the
//      pre-write board back on screen, and the member would watch their own
//      change disappear.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 PROGRESSIVE DISCLOSURE — T4's RULE, AND WHAT P5-3 T9 ADDED TO IT
// ═══════════════════════════════════════════════════════════════════════════
//
// T4 shipped this file rendering no destination concept at all, because at the
// time no trip could have two places: the compare screen did not exist and
// nothing could create a second one. T9 built the door, so this file now has to
// hold up the other three quarters of HANDOFF's rule as well. All four, and
// what each one is:
//
//  1. **At zero or one place, nothing changes.** No column, no grouping, no
//     empty state. Byte for byte the board T4 shipped, which is what
//     src/test/trips/progressive-disclosure.test.jsx still asserts, unmodified.
//
//  2. **The only door is one line in a menu you have to open.** The mock's own
//     note: "the only door in is one line in a menu you have to open." So the
//     menu's CONTENTS are conditionally rendered rather than hidden with CSS —
//     `<details>` would put "Compare another place" in the DOM of every
//     single-place board, which is the tax, and would also fail T4's test for
//     exactly the right reason.
//
//  3. **At two or more places with no winner, this board is not the surface.**
//     It redirects to the comparison. That is not a shortcut: an unfiltered
//     board would mix two places' cards in one STAY row with nothing to tell
//     them apart, and its roll-up would be "≈ £X each" for a trip that visits
//     BOTH — a figure for a trip nobody is planning. The comparison is the
//     honest render of that state and it already exists.
//
//  4. **Once a winner is chosen the board filters to it**, and the word
//     disappears again — HANDOFF: "the board filters to it and the word
//     'destination' disappears from the UI again." The figures come from the
//     service's per-destination roll-up, so filtering the cards and filtering
//     the arithmetic are the same act rather than two that can drift.

const ALL_OPEN = { stay: true, eat: false, do: false, travel: false }

export default function TripBoard({ section }) {
  const { tripId } = useParams()
  const navigate = useNavigate()
  const [state, setState] = useState({ status: 'loading', board: null, error: null })
  const [openRows, setOpenRows] = useState(ALL_OPEN)
  const [showRemoved, setShowRemoved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [comparing, setComparing] = useState(false)
  // 🔴 P6-82. A PREVIEW'S OUTCOME IS ITS OWN NOTICE, NOT `actionError`, AND
  // KEEPING THEM APART IS THE WHOLE POINT OF THE SECOND PIECE OF STATE.
  //
  // `actionError` means "the change you made to the board did not land" — the
  // member's response to it is to try the change again. A refused preview is
  // the opposite: the entry is on the board, the link is filed, and there is
  // nothing to redo. Putting a refusal in that notice would tell somebody
  // their write failed when it did not, which is a worse lie than saying
  // nothing at all. `previewNotice` is a separate string in a separate element
  // and neither one ever writes to the other.
  const [previewNotice, setPreviewNotice] = useState(null)
  // Which entry has a preview in flight, so a second press cannot spend the
  // service's per-user rate limit on a request already running.
  const [previewing, setPreviewing] = useState(null)
  // 🔴 P5-3 T10. ENRICHMENT IS ITS OWN PIECE OF STATE, ITS OWN REQUEST, AND
  // ITS OWN FAILURE. §6: *"Decoration may fail; **the grid may not.**"*
  //
  // It is deliberately NOT merged into `state.board`. If it were, the board's
  // status would become a function of three third-party providers, and the
  // obvious next edit — awaiting both in one `try` — would make a currency
  // feed's bad afternoon indistinguishable from the trip service being down.
  // `null` here means "not here", and every render path below works from it.
  const [enrichment, setEnrichment] = useState(null)

  useDocumentTitle(state.board?.trip?.title ?? section?.title ?? 'Trips')

  // 🔴 Every board read is stamped, and only the newest one is applied. Two
  // reads are now genuinely concurrent — a live refresh and the member's own
  // write — and without this the older answer can land last and put the
  // pre-write board back on screen. See the header.
  const generation = useRef(0)

  // 🔴 A SEPARATE READ, WITH ITS OWN CATCH, AND THE CATCH IS THE POINT.
  // A rejection here sets `null` — the board is already on screen and stays
  // there, and every enrichment field renders its designed absence. There is
  // no error notice, because a member cannot act on "the weather API is down"
  // and the grid is not affected by it.
  //
  // 🔴 IT RUNS AFTER EVERY BOARD READ, NOT ONLY ON MOUNT, AND THAT IS NOT AN
  // EXTRA. Enrichment is DERIVED FROM the board: add a stay, choose one, paste
  // an address, and the distances are about a different set of entries. A
  // once-on-mount fetch would leave a card that somebody just added with no
  // distance until they reloaded the page — on a screen whose whole argument
  // since T4 is that it updates without one.
  //
  // The cost of doing it is one round trip to our own Function, because the
  // service's answer comes out of `enrichment_cache` unless the board actually
  // changed in a way that needs a new provider call. Lazy (§6) is about not
  // computing on a schedule; it was never about computing once.
  //
  // Generation-tagged like the board's own refresh, for the same reason and
  // with the same failure if it were not: two reads are genuinely concurrent
  // here — a live refresh and the member's own write — and an older answer
  // landing last puts the pre-write enrichment back on screen.
  const enrichmentGeneration = useRef(0)
  const refreshEnrichment = useCallback(() => {
    const mine = enrichmentGeneration.current + 1
    enrichmentGeneration.current = mine
    return loadEnrichment(tripId).then(
      (answer) => {
        if (enrichmentGeneration.current === mine) setEnrichment(answer)
      },
      () => {
        if (enrichmentGeneration.current === mine) setEnrichment(null)
      },
    )
  }, [tripId])

  const refresh = useCallback(
    async ({ background = false } = {}) => {
      const mine = generation.current + 1
      generation.current = mine
      try {
        const board = await loadBoard(tripId)
        if (generation.current !== mine) return true
        setState({ status: 'ready', board, error: null })
        // Not awaited: the board is already on screen and the decoration
        // arrives when it arrives. Awaiting it here would make every write
        // wait on three third-party providers, which is §6 inverted.
        refreshEnrichment()
        return true
      } catch (error) {
        if (generation.current !== mine) return false
        // 🔴 A refresh nobody asked for never blanks a working board. §8: the
        // fallback engages with no user-visible ceremony, and a page that
        // replaces itself with an error notice because a background read
        // blipped is the largest ceremony available.
        if (background) return false
        setState({ status: 'error', board: null, error: error.message })
        return false
      }
    },
    [tripId, refreshEnrichment],
  )

  // The live channel's callback, through a ref so the channel is opened once
  // per trip rather than re-opened every time `refresh` is rebuilt.
  const refreshing = useRef(false)
  const again = useRef(false)
  const liveRefresh = useCallback(async () => {
    // Coalesced: a burst of changes is one re-read, and a change arriving
    // mid-read schedules exactly one more. Without this a busy minute is one
    // whole-board request per event.
    if (refreshing.current) {
      again.current = true
      return
    }
    refreshing.current = true
    try {
      do {
        again.current = false
        await refresh({ background: true })
      } while (again.current)
    } finally {
      refreshing.current = false
    }
  }, [refresh])

  const liveRefreshRef = useRef(liveRefresh)
  liveRefreshRef.current = liveRefresh

  const enrichmentRef = useRef(refreshEnrichment)
  enrichmentRef.current = refreshEnrichment

  useEffect(() => {
    let cancelled = false
    let channel = null
    loadBoard(tripId).then(
      (board) => {
        if (cancelled) return
        generation.current += 1
        setState({ status: 'ready', board, error: null })
        enrichmentRef.current()
        // Opened only after a successful load, and from THAT board's cursor —
        // which the Function took before it read the board, so the first thing
        // the channel may do is replay a change already on screen. That is one
        // redundant refresh and it is the price of never missing one.
        channel = openLiveChannel({
          tripId,
          cursor: board.cursor,
          onChange: () => liveRefreshRef.current(),
        })
      },
      (error) => {
        if (!cancelled) setState({ status: 'error', board: null, error: error.message })
      },
    )
    return () => {
      cancelled = true
      channel?.close()
    }
  }, [tripId])

  /** Every write goes through here: do it, then re-read the board.
   *
   * Re-reading rather than patching local state is the deliberate choice. A
   * write changes the estimate — coverage, the per-currency subtotals, and
   * which flat candidate the roll-up used — and none of that is derivable in
   * the browser without reimplementing cost.py. Patching one card and leaving
   * the figures stale is how a board ends up showing "amounts on 8 of 12"
   * beside thirteen cards.
   */
  const run = useCallback(
    async (work) => {
      setBusy(true)
      setActionError(null)
      // A new action clears a stale preview notice: it is about the previous
      // thing the member did and would otherwise sit under the new one.
      setPreviewNotice(null)
      try {
        await work()
        await refresh()
        return true
      } catch (error) {
        setActionError(error.message)
        return false
      } finally {
        setBusy(false)
      }
    },
    [refresh],
  )

  /** "Try for a preview" — P6-82.
   *
   * 🔴 DELIBERATELY NOT THROUGH `run`, AND NOT BECAUSE OF THE NOTICE. `run`
   * ends with `await refresh()`, and a refresh is exactly the wrong response
   * here: a stored photo already arrives on the changes feed, so re-reading the
   * board would be the second mechanism §5.6 forbids — and it would read the
   * board BEFORE the photo landed anyway, since the service commits the write
   * after this call returns. The right answer is to do nothing and let the tail
   * deliver it, which is what a live board is for.
   *
   * ⚠️ AND A REFUSAL MUST NOT LOOK LIKE A FAILED WRITE. `run` sets
   * `actionError` and returns false; the entry here is already on the board and
   * nothing needs redoing. See `previewNotice` above.
   */
  const onPreview = useCallback(async (entry) => {
    if (!entry?.id || previewing) return
    setPreviewing(entry.id)
    setActionError(null)
    try {
      await unfurlEntry(entry.id)
      // Says what was asked, and claims nothing about what is at the other end
      // — the card's three no-photo causes stay indistinguishable (§8), and a
      // page that simply has no photo on it is one of them.
      setPreviewNotice('Asked for a preview. If there is one, it will appear on the card.')
    } catch (error) {
      setPreviewNotice(error.message)
    } finally {
      setPreviewing(null)
    }
  }, [previewing])

  // 🔴 DERIVED BEFORE THE EARLY RETURNS, because two callbacks below close over
  // it and a hook cannot live behind an `if`. `splitPlaces(undefined)` answers
  // with three empty lists, which is the correct description of a board that
  // has not loaded.
  const places = splitPlaces(state.board?.destinations)

  /** Which place a NEW entry belongs to.
   *
   * 🔴 Attaching at write time is what keeps "loose" entries from existing.
   * The alternative — attach later, in bulk — means every entry added between
   * now and then is counted in no column on the comparison, and the compare
   * screen has to say so. It still can (a capped attach batch can leave some
   * behind), but this is the path that stops it being the normal case.
   *
   * `null` when the trip has no places at all, which is most trips and is the
   * shape the whole progressive-disclosure rule is built around.
   */
  const attachTo = places.chosen?.id ?? (places.live.length === 1 ? places.live[0].id : null)

  const onAdd = useCallback(
    (body) => run(() => addEntry(tripId, attachTo ? { ...body, destination_id: attachTo } : body)),
    [run, tripId, attachTo],
  )

  /** "Compare another place" — the one line in the menu.
   *
   * On success this LEAVES: the trip now has two places and this board is no
   * longer the surface for it (see 3 above). Navigating rather than re-reading
   * also means the member never sees the mixed, unlabelled board that would
   * exist for the half-second in between.
   */
  const onAddPlace = useCallback(
    async (body) => {
      const ok = await run(() => addPlace(tripId, body))
      if (ok) navigate(`/trips/${tripId}/compare`, { replace: true })
      return ok
    },
    [run, tripId, navigate],
  )
  const onVote = useCallback(
    (entry, level) => run(() => setPreference(entry.id, level)),
    [run],
  )
  const onRemove = useCallback(
    (entry) => run(() => editEntry(entry.id, { removed: true })),
    [run],
  )
  const onRestore = useCallback(
    (entry) => run(() => editEntry(entry.id, { removed: false })),
    [run],
  )

  const toggleRow = useCallback((key) => {
    setOpenRows((rows) => ({ ...rows, [key]: !rows[key] }))
  }, [])

  if (state.status === 'loading') {
    return (
      <article className="d2d-page">
        <p className="d2d-notice" data-testid="board-loading">
          Loading the board.
        </p>
      </article>
    )
  }

  if (state.status === 'error') {
    return (
      <article className="d2d-page">
        <p className="d2d-notice" data-testid="board-error">
          {state.error}
        </p>
        <p>
          <Link to="/trips">Back to your trips</Link>
        </p>
      </article>
    )
  }

  const { trip, entries, estimate, you } = state.board

  // 🔴 See 3 in the header. Two places on the table and no winner: this board
  // cannot render an honest figure for the trip, so it does not render one.
  if (places.live.length >= 2 && !places.chosen) {
    return <Navigate to={`/trips/${tripId}/compare`} replace />
  }

  // 🔴 See 4. `focus` is the chosen place or nothing, and it filters the CARDS
  // and the ARITHMETIC together — the roll-up comes from the service's
  // `by_destination` row for the same place, never from re-adding the cards
  // that are left.
  const focus = places.chosen ?? null
  const mine = focus
    ? entries.filter((entry) => entry.destination_id === focus.id)
    : entries
  const source = focus
    ? (estimate?.by_destination ?? []).find((row) => row.destination_id === focus.id)
    : estimate
  const live = mine.filter((entry) => !entry.removed)
  const removed = mine.filter((entry) => entry.removed)
  // A `Map<entryId, {distance}>`, or an empty one. Built once per render
  // rather than per card — and empty is the state for the whole first paint,
  // which is exactly what §6 means by the grid not depending on decoration.
  const distances = distancesByEntry(enrichment?.distance)

  return (
    <article className="d2d-page d2d-board">
      <header className="d2d-titleblock">
        <p className="d2d-eyebrow d2d-micro">
          <TierBadge tier={3} />
        </p>
        <p className="d2d-board__back">
          <Link to="/trips">← your trips</Link>
        </p>
        <h1 className="d2d-titleblock__headline">{trip.title}</h1>
        <p className="d2d-titleblock__lede">
          {trip.headcount} {trip.headcount === 1 ? 'person' : 'people'} · started by{' '}
          {trip.organiser_id === you ? 'you' : 'the crew'}
        </p>
        {/* P5-3 T6. The date poll is a screen of its own — mobile-first,
            because availability answers happen on phones — and this is the way
            in. It is stated as the trip's DATES rather than as a poll: a trip
            that has confirmed its range still has a dates screen, showing what
            was confirmed and who could not make it. */}
        <p className="d2d-board__dates">
          <Link to={`/trips/${trip.id}/dates`}>
            {trip.confirmed_range_start
              ? 'Dates · confirmed'
              : trip.state === 'dates_polling'
                ? 'Dates · answer the poll'
                : 'Dates'}
          </Link>
        </p>
        {/* P6-81. Who is on the trip — the invite picker, and the answer to
            "who can see this". Top level rather than behind the trip menu,
            because it is not a progressively-disclosed feature: it is the
            first thing a brand-new trip needs. The label says "just you" at a
            headcount of one on purpose — every trip started on this site is
            invitee-scoped with an empty list, which means visible to nobody
            else, and that state was previously invisible from every screen. */}
        <p className="d2d-board__people">
          <Link to={`/trips/${trip.id}/people`} data-testid="board-people-link">
            {trip.headcount === 1 ? 'People · just you' : `People · ${trip.headcount}`}
          </Link>
        </p>
        {/* P6-93. The link above is a HEADCOUNT — "one person is on this trip"
            — and a reader can absorb it without learning the other thing that
            is true, which is that nobody else can open the trip at all. Two
            different sentences, and only one of them was being said.

            🔴 IT IS NOT `headcount === 1`, which is what the link uses and
            what is correct FOR a headcount. This board's own fixture is a
            `crew` trip with no invitees: headcount one, and the whole server
            can open it. `src/trips/people.js` owns the predicate and the
            words; nothing about visibility is decided or worded here.

            The sentence is a fact, not a warning — organiser-only is the
            deliberate default (P6-92) — and it sits directly under the link to
            /people, which is where somebody changes it if they want to. */}
        {onlyYouCanSee(trip.visibility, trip.headcount) ? (
          <p className="d2d-board__visibility" data-testid="board-visibility">
            {visibilityLine(trip.visibility, trip.headcount)}
          </p>
        ) : null}
        {/* 🔴 THE ONLY DOOR, AND IT IS SHUT. The button says "Trip menu" and
            nothing else; what is behind it is rendered only once somebody opens
            it, so a single-place board's markup contains no reference to the
            feature at all. That is HANDOFF's rule and it is also why this is
            not a <details>: a closed <details> still puts its contents in the
            DOM, which is the tax the rule forbids and which T4's test would
            (correctly) fail on. */}
        <p className="d2d-tripmenu">
          <button
            type="button"
            className="d2d-tripmenu__toggle"
            aria-expanded={menuOpen}
            data-testid="trip-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            Trip menu
          </button>
        </p>
        {menuOpen ? (
          <div className="d2d-tripmenu__panel" data-testid="trip-menu-panel">
            {places.all.length >= 2 ? (
              <Link className="d2d-tripmenu__item" to={`/trips/${trip.id}/compare`}>
                {places.chosen ? 'What we didn’t pick' : 'The comparison'}
              </Link>
            ) : comparing ? (
              <AddPlace
                needsExisting={places.all.length === 0}
                onAdd={onAddPlace}
                busy={busy}
                onCancel={() => setComparing(false)}
              />
            ) : (
              <button
                type="button"
                className="d2d-tripmenu__item"
                data-testid="compare-door"
                onClick={() => setComparing(true)}
              >
                Compare another place
              </button>
            )}
          </div>
        ) : null}
        <hr className="d2d-rule" />
      </header>

      <Estimate
        estimate={{ overall: source?.overall }}
        headcount={trip.headcount}
        conversion={enrichment?.conversion}
      />

      {/* P5-3 T10. Rendered whatever state enrichment is in, including absent
          — the component's own first branch is the labeled absence, and `null`
          before the second request lands is the ordinary case rather than an
          error. */}
      <WeatherStrip
        weather={enrichment?.weather}
        range={{ start: trip.confirmed_range_start, end: trip.confirmed_range_end }}
      />

      {actionError ? (
        <p className="d2d-notice d2d-notice--warn" role="status" data-testid="board-action-error">
          {actionError}
        </p>
      ) : null}

      {/* P6-82. Its own element, so that a refused preview can never be read
          as a change that did not land. See `previewNotice` above. */}
      {previewNotice ? (
        <p className="d2d-notice" role="status" data-testid="board-preview-notice">
          {previewNotice}
        </p>
      ) : null}

      <AddEntry onAdd={onAdd} busy={busy} />

      <div className="d2d-board__rows">
        {CATEGORIES.map((category) => (
          <CategoryRow
            key={category.key}
            category={category}
            entries={live.filter((entry) => entry.category === category.key)}
            rollUp={source?.by_category?.[category.key]}
            open={Boolean(openRows[category.key])}
            onToggle={toggleRow}
            you={you}
            onVote={onVote}
            onRemove={onRemove}
            busy={busy}
            distances={distances}
            onPreview={onPreview}
            // 🔴 The id, not a boolean, so ONE card's button goes quiet rather
            // than the whole board. A preview spends up to six seconds on
            // somebody else's web server; disabling every vote on the page for
            // that long would make a decoration request feel like a write.
            previewing={previewing}
          />
        ))}
      </div>

      {/* 🔴 Hidden, never deleted. The toggle can only carry a count because
          the rows are still in the store — the board asks for them with
          `include_removed=true` and hides them here. "Put it back" is the same
          field set the other way. */}
      {removed.length > 0 ? (
        <section className="d2d-removed">
          <button
            type="button"
            className="d2d-removed__toggle d2d-micro"
            aria-expanded={showRemoved}
            onClick={() => setShowRemoved((value) => !value)}
          >
            {showRemoved ? `hide the ${removed.length} taken off` : `${removed.length} taken off`}
          </button>
          {showRemoved ? (
            <ul className="d2d-removed__list">
              {removed.map((entry) => (
                <li key={entry.id} className="d2d-removed__item">
                  <span className="d2d-removed__cat d2d-micro">{entry.category}</span>
                  <span className="d2d-removed__title">{entry.title}</span>
                  <button
                    type="button"
                    className="d2d-removed__restore"
                    disabled={busy}
                    onClick={() => onRestore(entry)}
                  >
                    Put it back
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </article>
  )
}
